import { type ComponentProps, forwardRef } from "react";
import { Input as BaseInput } from "@/Components/ui/input";

/**
 * The shadcn input with one behavioural guard: a focused `type="number"`
 * input steps its value on wheel scroll, so scrolling a form or grid can
 * silently rewrite a price or quantity with no visual cue. React registers
 * wheel listeners as passive, so `preventDefault()` is a no-op here —
 * dropping focus is the reliable guard.
 *
 * This wraps `@/Components/ui/input` rather than patching it, so the vendored
 * primitive stays regenerable via the shadcn CLI (see
 * `.agents/frontend/component-imports.md`). Importing `Input` from
 * `@/Components/Forms` — which every consumer is required to do — picks this
 * up transparently; the props are identical to the primitive's.
 */
export const Input = forwardRef<HTMLInputElement, ComponentProps<"input">>(
    ({ onWheel, ...props }, ref) => (
        <BaseInput
            ref={ref}
            onWheel={(e) => {
                if (props.type === "number" && e.currentTarget === document.activeElement) {
                    e.currentTarget.blur();
                }
                onWheel?.(e);
            }}
            {...props}
        />
    ),
);

Input.displayName = "Input";
